import { memo, useRef, useState } from 'react';
import { CheckCircle, CircleNotch, Image as ImageIcon, X } from '@phosphor-icons/react';
import {
  feedbackReceivedCopy,
  type FeedbackCategory,
  type FeedbackScreenshot,
} from '@ruta/shared';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ImageDropZone } from '@/components/ui/image-drop-zone';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { submitFeedback } from '@/lib/api/routes/feedback';
import { contentTypeForFile, fileToBase64 } from '@/lib/feedbackContext';

export interface ReportReceivedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueIdentifier: string;
  category: FeedbackCategory;
}

/**
 * Success modal after a real confirm-to-file (RUT-3900). Locked copy,
 * follow-up on the same Linear issue, notify-when-resolved (default on).
 */
export const ReportReceivedDialog = memo(function ReportReceivedDialog({
  open,
  onOpenChange,
  issueIdentifier,
  category,
}: ReportReceivedDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
        <ReportReceivedContent
          key={issueIdentifier}
          issueIdentifier={issueIdentifier}
          category={category}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
});

export function ReportReceivedContent({
  issueIdentifier,
  category,
  onDone,
}: {
  issueIdentifier: string;
  category: FeedbackCategory;
  onDone: () => void;
}) {
  const copy = feedbackReceivedCopy(category);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [notify, setNotify] = useState(true);
  const [notifySaving, setNotifySaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persistNotify = async (next: boolean) => {
    setNotify(next);
    setNotifySaving(true);
    try {
      await submitFeedback({
        issueIdentifier,
        notifyWhenResolved: next,
      });
    } catch {
      setNotify(!next);
      toast({
        title: 'Couldn’t update that',
        description: 'Try the notify switch again.',
        variant: 'destructive',
      });
    } finally {
      setNotifySaving(false);
    }
  };

  // Shared by the file picker and drag-and-drop/paste onto the attach row.
  const acceptScreenshot = (file: File): boolean => {
    if (!contentTypeForFile(file)) {
      setError('Screenshot must be a PNG, JPEG, or WebP image.');
      return false;
    }
    if (file.size > 1_500_000) {
      setError('Screenshot must be under 1.5 MB. Try a smaller image.');
      return false;
    }
    setError(null);
    setScreenshotFile(file);
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setScreenshotFile(null);
      return;
    }
    if (!acceptScreenshot(file)) e.target.value = '';
  };

  const handleDroppedFiles = (files: File[]) => {
    if (!files[0]) return;
    // Clear the picker so re-picking its previous file still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    acceptScreenshot(files[0]);
  };

  const sendFollowUp = async () => {
    const trimmed = followUp.trim();
    if (!trimmed) {
      setError('Please add a note.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      let screenshot: FeedbackScreenshot | undefined;
      if (screenshotFile) {
        const contentType = contentTypeForFile(screenshotFile);
        if (contentType) {
          screenshot = {
            contentType,
            dataBase64: await fileToBase64(screenshotFile),
          };
        }
      }
      await submitFeedback({
        issueIdentifier,
        message: trimmed,
        screenshot,
      });
      toast({
        title: copy.noun === 'feedback' ? 'Added to your feedback' : 'Added to your report',
        description: `Your note is on ${issueIdentifier}.`,
      });
      setAdding(false);
      setFollowUp('');
      setScreenshotFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We couldn’t add that. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <DialogHeader variant="bordered">
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5" weight="duotone" />
          {copy.title}
        </DialogTitle>
      </DialogHeader>

      <DialogBody>
        <DialogDescription className="text-sm text-muted-foreground">
          {copy.body}
        </DialogDescription>
        <p className="text-sm text-stone-700">
          Reference: <span className="font-medium">{issueIdentifier}</span>
        </p>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white p-4">
          <Label htmlFor="notify-resolved" className="cursor-pointer text-sm font-medium">
            {copy.notifyLabel}
          </Label>
          <Switch
            id="notify-resolved"
            checked={notify}
            disabled={notifySaving}
            onCheckedChange={(checked) => {
              void persistNotify(checked);
            }}
          />
        </div>

        {adding ? (
          <div className="space-y-2">
            <Label htmlFor="feedback-follow-up">{copy.followUpLabel}</Label>
            <Textarea
              id="feedback-follow-up"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              placeholder="Add a note or ask a question…"
              rows={3}
              disabled={sending}
              className="resize-none"
            />
            <ImageDropZone
              onFiles={handleDroppedFiles}
              disabled={sending}
              label="Drop screenshot"
              overlayClassName="rounded-md"
            >
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={sending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImageIcon className="mr-1.5 h-4 w-4" />
                  {screenshotFile ? 'Change image' : 'Attach image'}
                </Button>
                {screenshotFile && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800"
                    onClick={() => {
                      setScreenshotFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    disabled={sending}
                  >
                    <X className="h-3 w-3" />
                    {screenshotFile.name}
                  </button>
                )}
              </div>
            </ImageDropZone>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        ) : null}

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </DialogBody>

      <DialogFooter variant="bordered" className="gap-2">
        {adding ? (
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void sendFollowUp()} disabled={sending || !followUp.trim()}>
              {sending ? (
                <>
                  <CircleNotch className="mr-1.5 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send'
              )}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
              {copy.followUpLabel}
            </Button>
            <Button type="button" onClick={onDone}>
              Done
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}
